import { useCallback, useEffect, useMemo, useState } from 'react'

import { useNicheMutations } from 'hooks-queries/niche'
import { customerioTracker } from 'lib'
import { PanelStore, UserStore } from 'stores'
import { analyticsTracker } from 'utils'
import { getTerritoryLinkByType } from 'utils/territoryUtils'

import { CREATE_TERRITORY_NICHE_CONTEXT_HASH_LOG } from './niches-overview-section.consts'
import type { INichesOverviewSectionModel } from './niches-overview-section.types'
import { useNicheWithContext } from '../common/hooks'
import { SYSTEM_CONTEXT_IDS } from '../common/niche.consts'

export function useNicheOverviewSection({
  territoryId,
  setIsNichesEmpty,
  pageName,
  territoryTitle,
  providers,
  uploadDate,
  isTrendingTag,
  effectiveTerritoryId: externalEffectiveTerritoryId,
  feature,
}: INichesOverviewSectionModel) {
  const [contextHash, setContextHash] = useState<string>(null)

  const isUserViewer = UserStore.getRole === 'viewer'

  const effectiveTerritoryId = externalEffectiveTerritoryId !== undefined ? externalEffectiveTerritoryId : territoryId

  const [createNicheContextHash, { loading: isGeneratingHash, error }] = useNicheMutations({
    mutationName: 'createNicheContextHash',
    errorLog: CREATE_TERRITORY_NICHE_CONTEXT_HASH_LOG,
  })

  const createHashAndUpdate = useCallback(() => {
    if (isTrendingTag || !effectiveTerritoryId) return
    createNicheContextHash({
      variables: { input: { contextId: SYSTEM_CONTEXT_IDS.mostEngaged, territoryId: effectiveTerritoryId } },
      onCompleted: data => {
        const hash = data?.createNicheContextHash?.hash
        if (hash) {
          setContextHash(hash)
        }
      },
    })
  }, [createNicheContextHash, isTrendingTag, effectiveTerritoryId])

  const contextProps = useMemo(() => {
    return {
      providers,
      territoryId,
      uploadDateParam: uploadDate,
      contextHash,
      shouldGetPosts: true,
    }
  }, [contextHash, providers, territoryId, uploadDate])

  const { niches, nicheStatus, isNicheListInitialized, handleRefetch, hasError, unsubscribe } =
    useNicheWithContext(contextProps)

  useEffect(() => {
    createHashAndUpdate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTerritoryId])

  useEffect(() => {
    if (nicheStatus === 'completed') {
      unsubscribe()
    }
  }, [nicheStatus, unsubscribe])

  const nicheList = useMemo(() => {
    return niches
      .map(item => {
        return {
          territoryId: item.territoryId,
          name: item.name,
          posts: item.posts,
          avgEngagement: item.avgEngagement,
          topicIdOnSubtopic: '',
        }
      })
      .sort((a, b) => b.avgEngagement - a.avgEngagement)
      .slice(0, 5)
  }, [niches])

  const isWaitingForExternalTerritory = externalEffectiveTerritoryId !== undefined && !externalEffectiveTerritoryId

  const isLoading = useMemo(() => {
    return (
      isWaitingForExternalTerritory ||
      isGeneratingHash ||
      nicheStatus === 'pending' ||
      (!isNicheListInitialized && nicheStatus === 'completed')
    )
  }, [isWaitingForExternalTerritory, isGeneratingHash, nicheStatus, isNicheListInitialized])

  const hasErrors = useMemo(() => {
    return Boolean(error) || hasError || (nicheStatus === 'error' && !nicheList.length)
  }, [error, hasError, nicheStatus, nicheList])

  function handleTerritoryTrackClick(territory) {
    const { TOP } = analyticsTracker.eventNames
    analyticsTracker.eventTrack(TOP.CLICK_NICHE, {
      'Tab title': 'Overview',
      territory: territory?.name,
      'Page name': pageName,
      Title: territoryTitle,
    })
    customerioTracker.track(customerioTracker.eventNames.CLICK_NICHE, {
      feature,
      action: 'click_page',
      tab: 'overview',
      name: territory?.name,
      page_name: pageName,
    })
  }

  function getURLFromTerritory(territory) {
    if (isUserViewer) return '#'

    const panelId = (PanelStore?.selectedPanel as any)?.panelId
    const type = territory?.territoryId?.split('.')?.[0]
    const id = territory?.territoryId?.split('.')?.[1]

    const link = getTerritoryLinkByType(type, id, panelId)

    return `${link}/overview`
  }

  const territoriesRefetch = () => {
    if (error) {
      createHashAndUpdate()
      return
    }
    handleRefetch()
  }

  useEffect(() => {
    if ((!nicheList.length && !isLoading) || nicheStatus === 'empty' || isTrendingTag) {
      setIsNichesEmpty(true)
    }
  }, [isLoading, nicheList.length, nicheStatus, setIsNichesEmpty, isTrendingTag])

  return {
    territories: nicheList,
    isTerritoriesLoading: isLoading,
    territoriesError: hasErrors,
    isUserViewer,
    handleTerritoryTrackClick,
    getURLFromTerritory,
    territoriesRefetch,
  }
}
